import React, { createContext, useCallback, useContext, useState } from "react";
import { Check, AlertTriangle, X } from "lucide-react";

// 全域 Context：提供 showToast(msg) 與 confirm({ title, message, confirmText, danger }) -> Promise<boolean>
const ToastConfirmContext = createContext(null);

export const useToast = () => {
  const ctx = useContext(ToastConfirmContext);
  if (!ctx) throw new Error("useToast must be used within ToastConfirmProvider");
  return ctx.showToast;
};

export const useConfirm = () => {
  const ctx = useContext(ToastConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ToastConfirmProvider");
  return ctx.confirm;
};

export const ToastConfirmProvider = ({ children }) => {
  const [toast, setToast] = useState({ show: false, message: "" });
  const [confirmState, setConfirmState] = useState(null);

  const showToast = useCallback((message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: "" }), 2500);
  }, []);

  const confirm = useCallback(
    ({ title = "請確認", message = "", confirmText = "確定", cancelText = "取消", danger = false } = {}) => {
      return new Promise((resolve) => {
        setConfirmState({
          title,
          message,
          confirmText,
          cancelText,
          danger,
          resolve,
        });
      });
    },
    []
  );

  const handleConfirm = (val) => {
    if (confirmState) confirmState.resolve(val);
    setConfirmState(null);
  };

  return (
    <ToastConfirmContext.Provider value={{ showToast, confirm }}>
      {children}

      {/* Toast */}
      <div
        className={`fixed bottom-24 left-1/2 transform -translate-x-1/2 bg-slate-800 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-3 transition-all duration-300 z-[9999] ${
          toast.show
            ? "translate-y-0 opacity-100"
            : "translate-y-10 opacity-0 pointer-events-none"
        }`}
      >
        <div className="bg-green-500 rounded-full p-1">
          <Check size={12} className="text-white" />
        </div>
        <span className="font-bold text-sm">{toast.message}</span>
      </div>

      {/* Confirm Modal */}
      {confirmState && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-3">
              <div
                className={`p-2 rounded-full ${
                  confirmState.danger
                    ? "bg-red-100 text-red-500"
                    : "bg-blue-100 text-blue-500"
                }`}
              >
                <AlertTriangle size={18} />
              </div>
              <h3 className="font-bold text-lg text-slate-800 flex-1">
                {confirmState.title}
              </h3>
              <button
                onClick={() => handleConfirm(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={20} />
              </button>
            </div>
            {confirmState.message && (
              <p className="text-sm text-slate-600 mb-5 leading-relaxed whitespace-pre-line">
                {confirmState.message}
              </p>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => handleConfirm(false)}
                className="flex-1 bg-slate-100 text-slate-600 py-2.5 rounded-lg font-bold hover:bg-slate-200 transition-colors"
              >
                {confirmState.cancelText}
              </button>
              <button
                onClick={() => handleConfirm(true)}
                className={`flex-1 text-white py-2.5 rounded-lg font-bold shadow-md transition-colors ${
                  confirmState.danger
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {confirmState.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ToastConfirmContext.Provider>
  );
};
